import React, { useState, useEffect } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  getCommunityPostsApi,
  createCommunityPostApi,
  likeCommunityPostApi,
  getPostCommentsApi,
  addPostCommentApi,
  deleteCommunityPostApi
} from '../../../api/communities';
import DummyAvatar from '../../../assets/images/avatar.png';

export const CommunityFeed = ({ communityId, isOwner }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postText, setPostText] = useState('');
  const [postMedia, setPostMedia] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [expandedComments, setExpandedComments] = useState({});
  const [comments, setComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [commentErrors, setCommentErrors] = useState({});

  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    fetchPosts();
  }, [communityId]);

  const fetchPosts = async () => {
    const token = localStorage.getItem('authToken');
    const hasValidToken = token && token !== 'null' && token !== 'undefined';
    if (!hasValidToken) {
      setLoading(false);
      setPosts([]);
      return;
    }
    try {
      setLoading(true);
      const res = await getCommunityPostsApi(communityId);
      setPosts(res.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (!postText.trim() && !postMedia) return;
    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('type', 'post');
      if (postText) formData.append('text', postText);
      if (postMedia) formData.append('media', postMedia);
      await createCommunityPostApi(communityId, formData);
      setPostText('');
      setPostMedia(null);
      fetchPosts();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLike = async (postId) => {
    if (!user) { alert('Please login to like posts.'); return; }
    try {
      await likeCommunityPostApi(postId);
      setPosts(prevPosts =>
        prevPosts.map(post => {
          if (post._id === postId) {
            const likesList = post.likes || [];
            const hasLiked = likesList.some(likeId =>
              typeof likeId === 'object' ? likeId?._id === user._id : likeId === user._id
            );
            const updatedLikes = hasLiked
              ? likesList.filter(likeId => (typeof likeId === 'object' ? likeId?._id !== user._id : likeId !== user._id))
              : [...likesList, user._id];
            return { ...post, likes: updatedLikes };
          }
          return post;
        })
      );
    } catch (err) {
      console.error('Failed to like post', err);
    }
  };

  const fetchComments = async (postId) => {
    try {
      setLoadingComments(prev => ({ ...prev, [postId]: true }));
      const res = await getPostCommentsApi(postId);
      setComments(prev => ({ ...prev, [postId]: res.data || [] }));
    } catch (err) {
      setCommentErrors(prev => ({ ...prev, [postId]: 'Failed to load comments' }));
    } finally {
      setLoadingComments(prev => ({ ...prev, [postId]: false }));
    }
  };

  const toggleComments = (postId) => {
    const isExpanded = !!expandedComments[postId];
    setExpandedComments(prev => ({ ...prev, [postId]: !isExpanded }));
    if (!isExpanded) fetchComments(postId);
  };

  const handleCommentSubmit = async (e, postId) => {
    e.preventDefault();
    const commentText = commentInputs[postId] || '';
    if (!commentText.trim()) return;
    try {
      await addPostCommentApi(postId, { text: commentText.trim() });
      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      fetchComments(postId);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit comment');
    }
  };

  const handleCommentInputChange = (postId, val) => {
    setCommentInputs(prev => ({ ...prev, [postId]: val }));
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await deleteCommunityPostApi(postId);
      setPosts(prev => prev.filter(post => post._id !== postId));
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete post');
    }
  };

  const getTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const then = new Date(dateStr);
    const diffMs = now - then;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return diffMins <= 1 ? 'just now' : `${diffMins} minutes ago`;
      }
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    }
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 30) return `${diffDays} days ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `about ${diffMonths} month${diffMonths !== 1 ? 's' : ''} ago`;
  };

  return (
    <div className="community-feed-wrapper">
      {/* Loading */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: '#6C3BFF' }} />
        </div>
      ) : (
        <div className="d-flex flex-column gap-0">
          {posts.length === 0 ? (
            <div className="text-center py-5 rounded-4" style={{ background: '#fff', border: '1px solid #EAEAEA' }}>
              <i className="bi bi-chat-square-text d-block mb-2" style={{ fontSize: '2.5rem', color: '#6B7280', opacity: 0.5 }}></i>
              <h5 className="fw-bold" style={{ color: '#1A1A1A' }}>No posts yet</h5>
              <p style={{ color: '#6B7280', fontSize: '0.875rem' }}>Be the first to share something with this community.</p>
            </div>
          ) : (
            posts.map(post => {
              const authorId = typeof post.author === 'object' ? post.author?._id : post.author;
              const canDelete = isOwner || (user && authorId === user._id);
              const postLikes = post.likes || [];
              const hasLiked = user && postLikes.some(likeId =>
                typeof likeId === 'object' ? likeId?._id === user._id : likeId === user._id
              );

              return (
                <div key={post._id} className="post-card bg-white border rounded-3 p-3">
                  <div className="card-body p-0">
                    {/* Post Header */}
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <div className="d-flex align-items-center gap-2">
                        <img
                          src={post.author?.profile_picture || DummyAvatar}
                          alt={post.author?.full_name || 'Member'}
                          className="post-author-avatar user-avatar-lg"
                          onError={(e) => { e.target.src = DummyAvatar; }}
                        />
                        <div className="info">
                          <div className="post-author-name text-sm text-dark fw-bold">{post.author?.full_name || 'Community Member'}</div>
                          <div className="post-time text-sm text-secondary">{getTimeAgo(post.createdAt)}</div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {canDelete && (
                          <span className="text-danger pointer" onClick={() => handleDeletePost(post._id)} title="Delete Post">
                            <i className="bi bi-trash"></i>
                          </span>
                        )}
                        <span className="text-dark pointer" title="More Options">
                          <i className="bi bi-three-dots"></i>
                        </span>
                      </div>
                    </div>

                    {/* Post Text */}
                    {post.text && (
                      <div className="post-text text-sm mb-3" style={{ whiteSpace: 'pre-wrap' }}>{post.text}</div>
                    )}

                    {/* Post Media */}
                    {post.media && (() => {
                      const getMediaUrl = (media) => {
                        if (!media) return '';
                        if (typeof media === 'string') return media;
                        if (Array.isArray(media) && media.length > 0) return getMediaUrl(media[0]);
                        if (typeof media === 'object' && media !== null) return media.url || media.fileUrl || media.video_link || '';
                        return '';
                      };
                      const mediaUrl = getMediaUrl(post.media);
                      if (!mediaUrl) return null;
                      const isVideo = typeof mediaUrl === 'string' && mediaUrl.match(/\.(mp4|webm|ogg)$/i);
                      return (
                        <div className="rounded-3 overflow-hidden mb-3" style={{ background: '#F8F9FC' }}>
                          {isVideo
                            ? <video src={mediaUrl} controls className="w-100" style={{ maxHeight: '400px' }} />
                            : <img src={mediaUrl} alt="Post Attachment" className="w-100 object-fit-cover" style={{ maxHeight: '400px' }} />
                          }
                        </div>
                      );
                    })()}

                    {/* Post Actions */}
                    <div className="post-actions d-flex align-items-center column-gap-4">
                      <span
                        className={`action-btn d-flex align-items-center gap-1 pointer text-sm text-secondary ${hasLiked ? 'liked' : ''}`}
                        onClick={() => handleLike(post._id)}
                      >
                        <i className={`text-dark text-base bi ${hasLiked ? 'bi bi-hand-thumbs-up-fill' : 'bi bi-hand-thumbs-up'}`}></i>
                        {postLikes.length > 0 ? `${postLikes.length} Like${postLikes.length !== 1 ? 's' : ''}` : '0 Likes'}
                      </span>
                      <span className="action-btn d-flex align-items-center gap-1 pointer text-sm text-secondary" onClick={() => toggleComments(post._id)}>
                        <i className="text-dark text-base bi bi-chat-right-text"></i>
                        {(post.commentsCount || 0)} Comments
                      </span>
                    </div>

                    {/* Comments Section */}
                    {expandedComments[post._id] && (
                      <div className="mt-3 pt-3" style={{ borderTop: '1px solid #EAEAEA' }}>
                        {loadingComments[post._id] && (
                          <div className="text-center py-2">
                            <Spinner animation="border" size="sm" style={{ color: '#6C3BFF' }} />
                          </div>
                        )}
                        {commentErrors[post._id] && (
                          <small className="text-danger d-block mb-2">{commentErrors[post._id]}</small>
                        )}
                        {!loadingComments[post._id] && (
                          <div className="d-flex flex-column gap-2 mb-3">
                            {(comments[post._id] || []).length === 0 ? (
                              <small className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>No comments yet. Write one below!</small>
                            ) : (
                              comments[post._id].map(comment => (
                                <div key={comment._id} className="d-flex gap-2 align-items-start">
                                  <img
                                    src={comment.author?.profile_picture || DummyAvatar}
                                    alt="Avatar"
                                    className="rounded-circle object-fit-cover flex-shrink-0"
                                    style={{ width: '30px', height: '30px' }}
                                    onError={(e) => { e.target.src = DummyAvatar; }}
                                  />
                                  <div className="flex-grow-1 comment-bubble">
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <span className="comment-author">{comment.author?.full_name || 'Member'}</span>
                                      <span style={{ fontSize: '0.72rem', color: '#6B7280' }}>{getTimeAgo(comment.createdAt)}</span>
                                    </div>
                                    <p className="comment-text">{comment.text}</p>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                        {user && (
                          <Form onSubmit={(e) => handleCommentSubmit(e, post._id)} className="d-flex gap-2 align-items-center">
                            <img
                              src={user?.profile_picture || DummyAvatar}
                              alt="You"
                              className="rounded-circle object-fit-cover flex-shrink-0"
                              style={{ width: '32px', height: '32px' }}
                              onError={(e) => { e.target.src = DummyAvatar; }}
                            />
                            <Form.Control
                              type="text"
                              placeholder="Write a comment..."
                              className="rounded-pill px-3 py-2 border-0 shadow-none"
                              style={{ background: '#F8F9FC', fontSize: '0.82rem' }}
                              value={commentInputs[post._id] || ''}
                              onChange={(e) => handleCommentInputChange(post._id, e.target.value)}
                            />
                            <Button
                              type="submit"
                              size="sm"
                              className="rounded-circle d-flex align-items-center justify-content-center p-0 border-0 flex-shrink-0"
                              style={{ width: '34px', height: '34px', backgroundColor: '#6C3BFF' }}
                              disabled={!(commentInputs[post._id] || '').trim()}
                            >
                              <i className="bi bi-send-fill text-white" style={{ fontSize: '0.8rem' }}></i>
                            </Button>
                          </Form>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Create Post Card */}
      <div className="create-post-card bg-white border rounded-3 mt-3 p-3">
        <div className="card-body p-0">
          <div className="d-flex gap-2 align-items-start">
            <img
              src={user?.profile_picture || DummyAvatar}
              alt="Your avatar"
              className="user-avatar-lg"
              onError={(e) => { e.target.src = DummyAvatar; }}
            />
            <Form onSubmit={handlePostSubmit} className="flex-grow-1">
              <div className="upload-file-wrapper">
                <div className="wrapper d-flex gap-2">
                  <div className="input-group">
                    <Form.Control size='lg'
                      placeholder="Write something to the community..."
                      className="post-input-area text-sm"
                      style={{ resize: 'none', }}
                      value={postText}
                      onChange={(e) => setPostText(e.target.value)}
                    />
                    <span className="input-group-text bg-transparent">
                      <Form.Label
                        htmlFor="post-media-input"
                        className="btn-add-media m-0 pointer"
                      >
                        <i className="bi bi-image"></i>
                      </Form.Label>
                      <Form.Control
                        type="file"
                        id="post-media-input"
                        className="d-none"
                        accept="image/*,video/*"
                        onChange={(e) => setPostMedia(e.target.files[0])}
                      />
                    </span>
                    <span class="input-group-text bg-transparent">
                      <Form.Label
                        htmlFor="post-media-input"
                        className="m-0 pointer"
                      >
                        <i className="bi bi-emoji-smile"></i>
                      </Form.Label>
                    </span>
                  </div>
                  <Button
                    type="submit"
                    className="btn-post text-sm text-white"
                    style={{ backgroundColor: '#6C3BFF' }}
                    disabled={submitting || (!postText.trim() && !postMedia)}
                  >
                    {submitting ? 'Posting...' : 'Post'}
                  </Button>
                </div>
                {postMedia && (
                  <div className="text-sm text-muted text-truncate mt-2" style={{ maxWidth: '140px' }}>
                    {postMedia.name}
                  </div>
                )}
              </div>
            </Form>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && <div className="alert alert-danger text-sm text-center mt-3 py-2">{error}</div>}
    </div>
  );
};
